import { Button, Stack, Typography } from '@mui/material'
import { lighten } from '@mui/system'
import * as Sentry from '@sentry/react'
import React, { useMemo } from 'react'

import { isKubernetes } from 'components/attacks/create/targets/target-utils'
import Icon from 'components/Icon'
import { AddNodeMenu } from 'components/scenarios/concurrent/AddNodeMenu'
import { AttackGraphNode } from 'components/scenarios/concurrent/AttackGraphNode'
import { BranchActionsMenu } from 'components/scenarios/concurrent/BranchActionsMenu'
import {
  ActiveNodeIcon,
  LinearNodeIcon,
  NodeListIcon
} from 'components/scenarios/concurrent/ConcurrentTreeIcons'
import { ConcurrentTreeItem } from 'components/scenarios/concurrent/ConcurrentTreeItem'
import {
  DelayGraphNode,
  EditableDelayNode
} from 'components/scenarios/concurrent/DelayGraphNode'
import { FailureFlagGraphNode } from 'components/scenarios/concurrent/FailureFlagGraphNode'
import { useGraphEditorContext } from 'components/scenarios/concurrent/GraphEditorContext'
import { NodeActionsMenu } from 'components/scenarios/concurrent/NodeActionsMenu'
import { StatusCheckGraphNode } from 'components/scenarios/concurrent/StatusCheckGraphNode'
import {
  getGraphPathNestingLevel,
  getOrderedNodesFromBranch,
  GraphNodePath,
  isConcurrentNode,
  isDelayNode,
  isFailureFlagNode,
  isInfraAttackNode,
  isSynchronousStatusCheckNode,
  serializeNodePathToId
} from 'components/scenarios/utils/graph'
import {
  getColor,
  isActive
} from 'components/scenarios/utils/graph-node-lifecycle'
import { useFeatureFlags } from 'hooks/use-feature-flags'
import theme from 'theme'
import { isNumberStringEqual } from 'utils/strings'

import type { ApplicationClient } from 'types/api/alfi/clients'
import type { Client } from 'types/api/attacks/client'
import type { K8sClient } from 'types/api/kubernetes/kubernetes'
import type { ScenarioPreview } from 'types/api/reliability-tests/preview'
import type { ConcurrentNode, GraphNode } from 'types/api/scenarios/scenario'

const NodeIcon: React.FC<{
  color: string
  bgColor: string
  active: boolean
}> = ({ color, bgColor, active = false }) => {
  return active ? (
    <ActiveNodeIcon
      css={{
        border: `1px solid ${color}`,
        background: bgColor
      }}
    />
  ) : (
    <LinearNodeIcon
      css={{
        border: `1px solid ${color}`,
        background: bgColor
      }}
    />
  )
}

export const ConcurrentTreeViewNode: React.FC<{
  activeClients: Client[] | ApplicationClient[]
  hasRun?: boolean
  isClickable?: boolean
  isEditable?: boolean
  isFirstNode?: boolean
  isLastNode?: boolean
  k8sClients: K8sClient[]
  node: GraphNode
  onClick?: (node: GraphNode) => void
  scenarioPreview?: ScenarioPreview
}> = ({
  activeClients,
  hasRun,
  isClickable = false,
  isEditable = false,
  isFirstNode,
  isLastNode,
  k8sClients,
  node,
  onClick,
  scenarioPreview
}) => {
  const { maxNodeNesting: maxNestedNodeDepth } = useFeatureFlags()

  const {
    graphNodePaths,
    handleAddAttackNode,
    handleAddBranchAfter,
    handleAddCompletedAttack,
    handleAddConcurrentNode,
    handleAddDelayNode,
    handleAddExistingFailureFlagExperiment,
    handleAddFailureFlagNode,
    handleAddStatusCheckNode,
    handleDeleteConcurrentNode,
    handleMoveBranchDown,
    handleMoveBranchUp,
    handleMoveNodeDown,
    handleMoveNodeUp,
    handleEditNode,
    handleRemoveAttack,
    handleUpdateNode
  } = useGraphEditorContext()

  const isNodeActive = isActive(node.state?.lifecycle)
  const nodePath = graphNodePaths[node.guid]

  const statusColor = getColor(node.state?.lifecycle)
  const statusBgColor = lighten(statusColor, 0.9)

  const warningColor = theme.colors.orange[0]
  const warningBgColor = lighten(warningColor, 0.9)

  // TODO when expand/collapse is working
  // const hoverStatusColor = lighten(statusColor, 0.5)
  // const hoverStatusBgColor = lighten(statusBgColor, 0.5)

  const isConcurrentTreeViewNodeClickable = useMemo(() => {
    if (
      isClickable &&
      isInfraAttackNode(node) &&
      isKubernetes(
        node.attack_configuration?.targets[0]?.targetingStrategy.type
      )
    ) {
      // there's nothing useful to show when we don't have a k8s_attack_id, disable the ability to click on the node
      return Boolean(node.state?.k8s_attack_id)
    }
    return isClickable
  }, [isClickable, node])

  // concurrent node
  const isConcurrentTreeItemDisabled = useMemo(
    () => hasRun && !isConcurrentTreeViewNodeClickable,
    [hasRun, isConcurrentTreeViewNodeClickable]
  )

  const isStatusCheckNodeClickable = useMemo(() => {
    if (isClickable && node.state?.lifecycle === 'NotStarted') {
      return false
    }
    return isClickable
  }, [isClickable, node])

  const isStatusCheckNodeDisabled = useMemo(
    () => hasRun && !isStatusCheckNodeClickable,
    [hasRun, isStatusCheckNodeClickable]
  )

  if (isConcurrentNode(node)) {
    const concurrentNode: ConcurrentNode = node as ConcurrentNode // note: there may be a better way to do this without assertion
    const nodeBranches = concurrentNode.branches
    const isConcurrentNodeInvalid = nodeBranches.length < 2 // concurrent nodes should have more than one branch
    const concurrentNodeColor =
      isEditable && isConcurrentNodeInvalid ? warningColor : statusColor
    const concurrentNodeBgColor =
      isEditable && isConcurrentNodeInvalid ? warningBgColor : statusBgColor

    return (
      <ConcurrentTreeItem
        icon={
          <NodeListIcon
            css={{
              border: `1px solid ${concurrentNodeColor}`,
              background: concurrentNodeBgColor
              // TODO expand/collapse
              // '&:hover': {
              //   border: `1px solid ${hoverStatusColor}`,
              //   background: hoverStatusBgColor
              // }
            }}
          />
        }
        label="Concurrent Node"
        nodeId={node.guid}
      >
        {isEditable && (
          <ConcurrentTreeItem
            css={{ marginTop: theme.space[1], marginBottom: theme.space[3] }}
            label={
              <Stack direction="row" spacing={2}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={(e) => {
                    e.preventDefault()
                    handleDeleteConcurrentNode(graphNodePaths[node.guid])
                  }}
                >
                  Delete Concurrent Node
                </Button>
              </Stack>
            }
            nodeId={`${node.guid}-concurrent-node-controls`}
          />
        )}
        {nodeBranches.map((branch, i) => {
          // the path to the end of the node list for this branch
          const branchNewNodePath: GraphNodePath = [
            ...graphNodePaths[node.guid],
            'branches',
            i,
            'nodes',
            Object.keys(nodeBranches[i].nodes).length
          ]

          const nodeEntries: Array<[key: string | number, node: GraphNode]> =
            getOrderedNodesFromBranch(branch)
          const firstNodeKey = branch.start_id

          const numNestedBranches = getGraphPathNestingLevel(branchNewNodePath)
          const isGraphNestingTooDeep = numNestedBranches >= maxNestedNodeDepth
          const isBranchInvalid = isEditable && nodeEntries.length < 1

          return (
            <ConcurrentTreeItem
              key={`${node.guid}-branch-${i}`}
              css={{
                marginTop: theme.space[1],
                marginBottom: theme.space[3]
              }}
              disabled={isConcurrentTreeItemDisabled}
              label={<Typography>{`Branch ${i + 1}`}</Typography>}
              nodeId={`${node.guid}-branch-${i}`}
            >
              {isEditable && (
                <ConcurrentTreeItem
                  css={{
                    marginTop: theme.space[1],
                    marginBottom: theme.space[3]
                  }}
                  label={
                    <BranchActionsMenu
                      branchNodePath={[
                        ...graphNodePaths[node.guid],
                        'branches',
                        i
                      ]}
                      // only allow move branch down if this isn't the last branch
                      onMoveBranchDown={
                        i < nodeBranches.length - 1
                          ? handleMoveBranchDown
                          : undefined
                      }
                      // only allow moving branch up if this isn't the first branch
                      onMoveBranchUp={i > 0 ? handleMoveBranchUp : undefined}
                    />
                  }
                  nodeId={`${node.guid}-branch-${i}-branch-menu`}
                />
              )}
              {nodeEntries.map(([nodeKey, node]) => {
                let isFirstNode = false
                let isLastNode = false

                if (nodeEntries.length === 1) {
                  isLastNode = true
                  isFirstNode = true
                } else {
                  isFirstNode = isNumberStringEqual(nodeKey, firstNodeKey)
                  isLastNode = !node.next
                }

                return (
                  <ConcurrentTreeViewNode
                    key={node.guid}
                    activeClients={activeClients}
                    hasRun={hasRun}
                    isClickable={isConcurrentTreeViewNodeClickable}
                    isEditable={isEditable}
                    isFirstNode={isFirstNode}
                    isLastNode={isLastNode}
                    k8sClients={k8sClients}
                    node={node}
                    scenarioPreview={scenarioPreview}
                    onClick={onClick}
                  />
                )
              })}
              {isEditable && nodeEntries.length < 1 && (
                <ConcurrentTreeItem
                  css={{
                    marginTop: theme.space[4],
                    marginBottom: theme.space[4]
                  }}
                  label="There are no nodes in this branch. Use the menu below to add one."
                  nodeId={`${node.guid}-branch-${i}-empty-message`}
                />
              )}
              {isEditable && (
                <ConcurrentTreeItem
                  css={{
                    marginTop: theme.space[3],
                    marginBottom: theme.space[3]
                  }}
                  label={
                    <AddNodeMenu
                      color={isBranchInvalid ? 'warning' : undefined}
                      id={serializeNodePathToId(branchNewNodePath)}
                      label="Add Node"
                      onAddAttackNode={() =>
                        handleAddAttackNode(branchNewNodePath)
                      }
                      onAddCompletedAttack={() =>
                        handleAddCompletedAttack(branchNewNodePath)
                      }
                      onAddConcurrentNode={
                        isGraphNestingTooDeep
                          ? undefined
                          : () => handleAddConcurrentNode(branchNewNodePath)
                      }
                      onAddDelayNode={() =>
                        handleAddDelayNode(branchNewNodePath)
                      }
                      onAddExistingFailureFlagExperiment={() =>
                        handleAddExistingFailureFlagExperiment(
                          branchNewNodePath
                        )
                      }
                      onAddFailureFlagNode={() =>
                        handleAddFailureFlagNode(branchNewNodePath)
                      }
                      onAddStatusCheckNode={() =>
                        handleAddStatusCheckNode(branchNewNodePath)
                      }
                    />
                  }
                  nodeId={`${node.guid}-branch-${i}-add-menu`}
                />
              )}
            </ConcurrentTreeItem>
          )
        })}
        {isEditable && (
          <ConcurrentTreeItem
            css={{ marginTop: theme.space[3], marginBottom: theme.space[3] }}
            label={
              <Stack direction="row" spacing={2}>
                <Button
                  color={isConcurrentNodeInvalid ? 'warning' : undefined}
                  size="small"
                  variant="outlined"
                  onClick={(e) => {
                    e.preventDefault()
                    const branchNodePath: GraphNodePath = [
                      ...graphNodePaths[node.guid],
                      'branches',
                      nodeBranches.length
                    ]
                    handleAddBranchAfter(branchNodePath)
                  }}
                >
                  Add Branch
                </Button>
              </Stack>
            }
            nodeId={`${node.guid}-concurrent-node-controls`}
          />
        )}
      </ConcurrentTreeItem>
    )
  }

  if (isDelayNode(node)) {
    return (
      <ConcurrentTreeItem
        endIcon={
          <NodeIcon
            active={isNodeActive}
            bgColor={statusBgColor}
            color={statusColor}
          />
        }
        label={
          isEditable ? (
            <EditableDelayNode
              node={node}
              rowActions={
                isEditable ? (
                  <NodeActionsMenu
                    id={serializeNodePathToId(nodePath)}
                    // only allow moving node down if this isn't the last branch
                    onMoveDown={
                      isLastNode
                        ? undefined
                        : () => handleMoveNodeDown(nodePath)
                    }
                    // only allow moving node up if this isn't the first branch
                    onMoveUp={
                      isFirstNode ? undefined : () => handleMoveNodeUp(nodePath)
                    }
                    onRemove={() => handleRemoveAttack(node)}
                  />
                ) : null
              }
              onUpdate={(delay) => handleUpdateNode({ ...node, delay })}
            />
          ) : (
            <DelayGraphNode delay={node.delay} />
          )
        }
        nodeId={node.guid}
      />
    )
  }

  if (isSynchronousStatusCheckNode(node)) {
    return (
      <ConcurrentTreeItem
        disabled={isStatusCheckNodeDisabled}
        endIcon={
          <NodeIcon
            active={isNodeActive}
            bgColor={statusBgColor}
            color={statusColor}
          />
        }
        label={
          <StatusCheckGraphNode
            isClickable={isStatusCheckNodeClickable}
            node={node}
            rowActions={
              isEditable ? (
                <NodeActionsMenu
                  id={serializeNodePathToId(nodePath)}
                  onEdit={() => handleEditNode(node)}
                  // only allow moving node down if this isn't the last branch
                  onMoveDown={
                    isLastNode ? undefined : () => handleMoveNodeDown(nodePath)
                  }
                  // only allow moving node up if this isn't the first branch
                  onMoveUp={
                    isFirstNode ? undefined : () => handleMoveNodeUp(nodePath)
                  }
                  onRemove={() => handleRemoveAttack(node)}
                />
              ) : isClickable ? (
                <Icon icon="chevronRight" />
              ) : null
            }
            onClick={onClick}
          />
        }
        nodeId={node.guid}
      />
    )
  }

  const rowActions = isEditable ? (
    <NodeActionsMenu
      id={serializeNodePathToId(nodePath)}
      onEdit={() => handleEditNode(node)}
      // only allow moving node down if this isn't the last branch
      onMoveDown={isLastNode ? undefined : () => handleMoveNodeDown(nodePath)}
      // only allow moving node up if this isn't the first branch
      onMoveUp={isFirstNode ? undefined : () => handleMoveNodeUp(nodePath)}
      onRemove={() => handleRemoveAttack(node)}
    />
  ) : isClickable ? (
    <Icon icon="chevronRight" />
  ) : null

  if (isInfraAttackNode(node)) {
    return (
      <ConcurrentTreeItem
        data-testid="concurrent-tree-item-attack-graph-node"
        disabled={isConcurrentTreeItemDisabled}
        endIcon={
          <NodeIcon
            active={isNodeActive}
            bgColor={statusBgColor}
            color={statusColor}
          />
        }
        label={
          <AttackGraphNode
            allNodes={[node]}
            isClickable={isConcurrentTreeViewNodeClickable}
            node={node}
            rowActions={rowActions}
            scenarioPreview={scenarioPreview}
            onClick={onClick}
          />
        }
        nodeId={node.guid}
      />
    )
  }

  if (isFailureFlagNode(node)) {
    return (
      <ConcurrentTreeItem
        disabled={isConcurrentTreeItemDisabled}
        endIcon={
          <NodeIcon
            active={isNodeActive}
            bgColor={statusBgColor}
            color={statusColor}
          />
        }
        label={
          <FailureFlagGraphNode
            isClickable={isConcurrentTreeViewNodeClickable}
            node={node}
            rowActions={rowActions}
            onClick={onClick}
          />
        }
        nodeId={node.guid}
      />
    )
  }

  // this shouldn't ever be reached, but just in case it somehow happens, we'll at least know
  // note: this doesn't need to be translated because it's only sent to us for debugging
  Sentry.captureMessage(
    `Invalid scenario graph node found in ConcurrentTreeViewNode: ${JSON.stringify(
      node
    )}`
  )

  return null
}
